export const MATERIAL_VALIDATION_CASE_STATUSES = new Set([
	'PENDING_REVIEW',
	'APPROVED',
	'RETURNED_FOR_CORRECTION',
	'SUPERSEDED',
	'CANCELED',
]);

export const MATERIAL_VALIDATION_CONSISTENCY_STATES = new Set([
	'CONSISTENT',
	'INCONSISTENT',
]);

const MATERIAL_VALIDATION_DECISIONS = new Set([
	'APPROVE',
	'RETURN_FOR_CORRECTION',
]);

const MATERIAL_VALIDATION_EVALUATION_STATUSES = new Set([
	'MATCH',
	'MISMATCH',
	'INDETERMINATE',
]);
const INVENTORY_CHANGE_KINDS = new Set([
	'BULK',
	'SERIAL_ADDED',
	'SERIAL_REMOVED',
	'SERIAL_RECATALOGED',
	'SERIAL_UPDATED',
]);
const APPROVAL_QUANTITY_STRATEGIES = new Set([
	'PAY_EXPECTED',
	'PAY_REPORTED',
	'PAY_ADJUSTED',
]);
const CURRENT_INVENTORY_KINDS = new Set(['BULK', 'SERIAL']);
const CURRENT_INVENTORY_UNAVAILABLE_REASONS = new Set([
	'CURRENT_WTD_SEAL_INVALID',
	'AMBIGUOUS_SERIALIZED_INVENTORY',
]);

const SHA256_PATTERN = /^[a-f0-9]{64}$/;
const REASON_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,99}$/;
const ISO_DATE_TIME_PATTERN =
	/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
export const TECHNICIAN_INVENTORY_RESOLUTION_KEY =
	'FINALIZE_TECHNICIAN_INVENTORY';
export const GPON_INVENTORY_RECONCILIATION_RESOLUTION_KEY =
	'RECONCILE_GPON_INSTALLATION_PRICING';
export const MANUAL_INVENTORY_ADJUSTMENT_RESOLUTION_KEY =
	'APPLY_MANUAL_INVENTORY_ADJUSTMENT';

// Returning a manual case enables inventory edits; it does not close the case.
export const isManualCorrectionCase = materialCase =>
	materialCase?.resolutionKey === MANUAL_INVENTORY_ADJUSTMENT_RESOLUTION_KEY &&
	materialCase.scopeKey === 'manual_inventory_correction' &&
	materialCase.input?.sourceKey === 'manual_material_correction_request';

export const isConfirmedManualCorrectionCase = materialCase =>
	isManualCorrectionCase(materialCase) &&
	materialCase.status === 'PENDING_REVIEW' &&
	Number.isSafeInteger(materialCase.version) &&
	materialCase.version >= 2 &&
	materialCase.latestDecision?.action === 'RETURN_FOR_CORRECTION';

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const hasReadyLegacyManualEvaluation = viewModel =>
	isManualCorrectionCase(viewModel?.currentCase) &&
	viewModel.currentCase.status === 'PENDING_REVIEW' &&
	Number.isSafeInteger(viewModel.currentCase.version) &&
	viewModel.currentCase.version >= 2 &&
	viewModel.currentEvaluation?.targetIsAdjustment === true &&
	Array.isArray(viewModel.currentEvaluation.inventoryChanges) &&
	viewModel.currentEvaluation.inventoryChanges.length > 0;

export const getManualAdjustmentState = viewModel => {
	if (!isManualCorrectionCase(viewModel?.currentCase)) return null;
	if (hasOwn(viewModel?.workflow || {}, 'manualAdjustment'))
		return viewModel.workflow.manualAdjustment?.state || null;
	return hasReadyLegacyManualEvaluation(viewModel)
		? 'READY_FOR_REVIEW'
		: 'EDITING';
};

export const isManualCorrectionWaiting = viewModel =>
	isConfirmedManualCorrectionCase(viewModel?.currentCase) &&
	getManualAdjustmentState(viewModel) === 'EDITING';

export const isManualCorrectionReadyForReview = viewModel =>
	isManualCorrectionCase(viewModel?.currentCase) &&
	getManualAdjustmentState(viewModel) === 'READY_FOR_REVIEW';

export const isValidMaterialEvaluationSha256 = value =>
	typeof value === 'string' && SHA256_PATTERN.test(value);

export class InvalidMaterialValidationContractError extends Error {
	constructor(message) {
		super(message);
		this.name = 'InvalidMaterialValidationContractError';
	}
}

const invalid = message => {
	throw new InvalidMaterialValidationContractError(message);
};

const hasIdentifier = value =>
	(typeof value === 'string' && value.length > 0) ||
	(typeof value === 'number' && Number.isFinite(value));

const isRecord = value =>
	!!value && typeof value === 'object' && !Array.isArray(value);

const validateComparison = (comparison, path) => {
	if (!isRecord(comparison)) invalid(`${path} debe ser un objeto.`);
	if (!Number.isFinite(comparison.ordinal))
		invalid(`${path}.ordinal es requerido.`);
	if (!hasIdentifier(comparison.metricKey))
		invalid(`${path}.metricKey es requerido.`);
	if (!isRecord(comparison.subject)) invalid(`${path}.subject es requerido.`);
	if (!isRecord(comparison.unit)) invalid(`${path}.unit es requerida.`);
	if (!['ITEM', 'AGGREGATE'].includes(comparison.subject.kind))
		invalid(`${path}.subject.kind no es compatible.`);
	if (!hasIdentifier(comparison.subject.code))
		invalid(`${path}.subject.code es requerido.`);
	if (!hasIdentifier(comparison.subject.label))
		invalid(`${path}.subject.label es requerido.`);
	if (
		comparison.subject.kind === 'AGGREGATE' &&
		comparison.subject.itemId !== null
	)
		invalid(`${path}.subject.itemId debe ser null para un agregado.`);
	if (
		comparison.subject.kind === 'ITEM' &&
		!hasIdentifier(comparison.subject.itemId)
	)
		invalid(`${path}.subject.itemId es requerido para un material.`);
	if (comparison.unit.id !== null && !hasIdentifier(comparison.unit.id))
		invalid(`${path}.unit.id no es compatible.`);
	if (comparison.subject.kind === 'ITEM' && !hasIdentifier(comparison.unit.id))
		invalid(`${path}.unit.id es requerida para un material.`);
	if (
		typeof comparison.unit.code !== 'string' ||
		comparison.unit.code.trim().length === 0
	)
		invalid(`${path}.unit.code es requerida.`);
	if (!Number.isFinite(comparison.expectedValue))
		invalid(`${path}.expectedValue debe ser numérico.`);
	if (!Number.isFinite(comparison.reportedValue))
		invalid(`${path}.reportedValue debe ser numérico.`);
	if (!['EQ', 'LTE', 'GTE'].includes(comparison.operator))
		invalid(`${path}.operator no es compatible.`);
	if (
		comparison.toleranceValue !== null &&
		!Number.isFinite(comparison.toleranceValue)
	)
		invalid(`${path}.toleranceValue no es compatible.`);
	if (
		comparison.unit.code === 'BOOLEAN' &&
		(![0, 1].includes(comparison.expectedValue) ||
			![0, 1].includes(comparison.reportedValue))
	)
		invalid(`${path} contiene un BOOLEAN distinto de 0 o 1.`);
	if (!['MATCH', 'MISMATCH'].includes(comparison.outcome))
		invalid(`${path}.outcome no es compatible.`);
};

const CANONICAL_DECIMAL_PATTERN = /^(?:0|[1-9]\d*)(?:\.\d{0,5}[1-9])?$/;

const compareCanonicalDecimals = (left, right) => {
	const [leftInteger, leftFraction = ''] = left.split('.');
	const [rightInteger, rightFraction = ''] = right.split('.');
	if (leftInteger.length !== rightInteger.length)
		return leftInteger.length < rightInteger.length ? -1 : 1;
	if (leftInteger !== rightInteger) return leftInteger < rightInteger ? -1 : 1;
	const normalizedLeftFraction = `${leftFraction}000000`.slice(0, 6);
	const normalizedRightFraction = `${rightFraction}000000`.slice(0, 6);
	if (normalizedLeftFraction === normalizedRightFraction) return 0;
	return normalizedLeftFraction < normalizedRightFraction ? -1 : 1;
};

const validatePersistedPricingResolution = (resolution, path) => {
	if (!isRecord(resolution)) invalid(`${path} debe ser un objeto o null.`);
	if (resolution.mode !== 'PER_COMPARISON')
		invalid(`${path}.mode no es compatible.`);
	if (!Array.isArray(resolution.selections))
		invalid(`${path}.selections debe ser un arreglo.`);
	const seenMetricKeys = new Set();
	resolution.selections.forEach((selection, index) => {
		const selectionPath = `${path}.selections[${index}]`;
		if (!isRecord(selection)) invalid(`${selectionPath} debe ser un objeto.`);
		if (!hasIdentifier(selection.metricKey))
			invalid(`${selectionPath}.metricKey es requerido.`);
		const metricKey = String(selection.metricKey);
		if (seenMetricKeys.has(metricKey))
			invalid(`${path}.selections contiene metricKey duplicado.`);
		seenMetricKeys.add(metricKey);
		if (!['MATCH', 'MISMATCH'].includes(selection.outcome))
			invalid(`${selectionPath}.outcome no es compatible.`);
		if (!isRecord(selection.material))
			invalid(`${selectionPath}.material es requerido.`);
		if (!hasIdentifier(selection.material.itemId))
			invalid(`${selectionPath}.material.itemId es requerido.`);
		if (!hasIdentifier(selection.material.code))
			invalid(`${selectionPath}.material.code es requerido.`);
		if (!hasIdentifier(selection.material.label))
			invalid(`${selectionPath}.material.label es requerido.`);
		if (
			!isRecord(selection.unit) ||
			!hasIdentifier(selection.unit.id) ||
			typeof selection.unit.code !== 'string' ||
			!selection.unit.code.trim() ||
			selection.unit.code === 'BOOLEAN'
		)
			invalid(`${selectionPath}.unit no es compatible.`);
		['expectedValue', 'reportedValue', 'resolvedValue'].forEach(key => {
			if (
				typeof selection[key] !== 'string' ||
				!CANONICAL_DECIMAL_PATTERN.test(selection[key])
			)
				invalid(`${selectionPath}.${key} no es un decimal canónico.`);
		});
		if (!hasOwn(selection, 'adjustedValue'))
			invalid(`${selectionPath}.adjustedValue es requerido.`);

		if (selection.selectionSource === 'SYSTEM_MATCH') {
			if (
				selection.outcome !== 'MATCH' ||
				selection.expectedValue !== selection.reportedValue ||
				selection.strategy !== null ||
				selection.adjustedValue !== null ||
				selection.justification !== null
			)
				invalid(`${selectionPath} no representa una coincidencia automática.`);
			return;
		}
		if (selection.selectionSource !== 'ADMIN_SELECTION')
			invalid(`${selectionPath}.selectionSource no es compatible.`);
		if (
			selection.outcome !== 'MISMATCH' ||
			!APPROVAL_QUANTITY_STRATEGIES.has(selection.strategy)
		)
			invalid(`${selectionPath} no representa una selección administrativa.`);
		if (selection.strategy === 'PAY_ADJUSTED') {
			if (
				typeof selection.adjustedValue !== 'string' ||
				!CANONICAL_DECIMAL_PATTERN.test(selection.adjustedValue) ||
				typeof selection.justification !== 'string' ||
				selection.justification.trim().length < 10 ||
				selection.justification.trim().length > 500
			)
				invalid(`${selectionPath}.justification no es válida.`);
			if (
				compareCanonicalDecimals(
					selection.resolvedValue,
					selection.adjustedValue,
				) > 0
			)
				invalid(`${selectionPath}.resolvedValue excede adjustedValue.`);
		} else if (
			selection.adjustedValue !== null ||
			selection.justification !== null
		) {
			invalid(`${selectionPath}.adjustedValue y justification deben ser null.`);
		}
		if (
			selection.strategy === 'PAY_EXPECTED' &&
			compareCanonicalDecimals(
				selection.resolvedValue,
				selection.expectedValue,
			) > 0
		)
			invalid(`${selectionPath}.resolvedValue excede expectedValue.`);
		if (
			selection.strategy === 'PAY_REPORTED' &&
			compareCanonicalDecimals(
				selection.resolvedValue,
				selection.reportedValue,
			) > 0
		)
			invalid(`${selectionPath}.resolvedValue excede reportedValue.`);
	});
};

const validateLatestDecision = (decision, path) => {
	if (decision === null) return;
	if (!isRecord(decision)) invalid(`${path} debe ser un objeto o null.`);
	if (!hasIdentifier(decision.action)) invalid(`${path}.action es requerida.`);
	if (!hasIdentifier(decision.createdAt))
		invalid(`${path}.createdAt es requerida.`);
	const hasPricingResolution = hasOwn(decision, 'pricingResolution');
	const hasApplicationMode = hasOwn(decision, 'applicationMode');
	if (!hasPricingResolution && !hasApplicationMode) return;
	if (!hasPricingResolution || !hasApplicationMode)
		invalid(`${path} debe incluir pricingResolution y applicationMode.`);
	if (
		decision.applicationMode !== null &&
		!['MUTABLE_REPRICE', 'ADVISORY_ONLY'].includes(decision.applicationMode)
	)
		invalid(`${path}.applicationMode no es compatible.`);
	if (decision.pricingResolution === null) return;
	if (!['MUTABLE_REPRICE', 'ADVISORY_ONLY'].includes(decision.applicationMode))
		invalid(`${path}.applicationMode es requerido para pricingResolution.`);
	if (decision.action !== 'APPROVE')
		invalid(`${path}.pricingResolution sólo corresponde a una aprobación.`);
	validatePersistedPricingResolution(
		decision.pricingResolution,
		`${path}.pricingResolution`,
	);
};

const validateCase = (materialCase, path, { requireVersion = false } = {}) => {
	if (
		!materialCase ||
		typeof materialCase !== 'object' ||
		Array.isArray(materialCase)
	)
		invalid(`${path} debe ser un objeto.`);
	if (!hasIdentifier(materialCase.id)) invalid(`${path}.id es requerido.`);
	if (
		!Number.isSafeInteger(materialCase.occurrence) ||
		materialCase.occurrence < 1
	)
		invalid(`${path}.occurrence debe ser un entero positivo.`);
	if (!MATERIAL_VALIDATION_CASE_STATUSES.has(materialCase.status))
		invalid(`${path}.status no es compatible.`);
	if (requireVersion && !hasIdentifier(materialCase.version))
		invalid(`${path}.version es requerida.`);
	if (!Array.isArray(materialCase.comparisons))
		invalid(`${path}.comparisons debe ser un arreglo.`);
	materialCase.comparisons.forEach((comparison, index) =>
		validateComparison(comparison, `${path}.comparisons[${index}]`),
	);
	if (!Object.prototype.hasOwnProperty.call(materialCase, 'latestDecision'))
		invalid(`${path}.latestDecision es requerida.`);
	validateLatestDecision(materialCase.latestDecision, `${path}.latestDecision`);
};

export const requiresLiveMaterialEvaluation = (materialCase, evaluation) =>
	materialCase?.resolutionKey === TECHNICIAN_INVENTORY_RESOLUTION_KEY ||
	(materialCase?.resolutionKey ===
		GPON_INVENTORY_RECONCILIATION_RESOLUTION_KEY &&
		evaluation != null) ||
	(isManualCorrectionCase(materialCase) && evaluation != null);

const validateInventoryChangeSide = (side, path) => {
	if (!isRecord(side)) invalid(`${path} debe ser un objeto.`);
	if (!isRecord(side.item)) invalid(`${path}.item es requerido.`);
	if (!hasIdentifier(side.item.itemId))
		invalid(`${path}.item.itemId es requerido.`);
	if (!hasIdentifier(side.item.code))
		invalid(`${path}.item.code es requerido.`);
	if (!hasIdentifier(side.item.label))
		invalid(`${path}.item.label es requerido.`);
	if (!Number.isFinite(side.value)) invalid(`${path}.value debe ser numérico.`);
};

const validateInventoryChange = (change, path) => {
	if (!isRecord(change)) invalid(`${path} debe ser un objeto.`);
	if (!INVENTORY_CHANGE_KINDS.has(change.kind))
		invalid(`${path}.kind no es compatible.`);
	if (!isRecord(change.unit)) invalid(`${path}.unit es requerida.`);
	if (!hasIdentifier(change.unit.id)) invalid(`${path}.unit.id es requerida.`);
	if (!hasIdentifier(change.unit.code))
		invalid(`${path}.unit.code es requerida.`);
	if (!Number.isFinite(change.delta))
		invalid(`${path}.delta debe ser numérico.`);
	if (hasOwn(change, 'changeType')) {
		if (!['STATE_CHANGED', 'SERIAL_SUBSTITUTED'].includes(change.changeType))
			invalid(`${path}.changeType no es compatible.`);
		if (!['SERIAL_ADDED', 'SERIAL_REMOVED'].includes(change.kind))
			invalid(`${path}.changeType no corresponde al tipo de cambio.`);
	}
	if (
		hasOwn(change, 'seriesId') &&
		change.seriesId !== null &&
		!hasIdentifier(change.seriesId)
	)
		invalid(`${path}.seriesId no es compatible.`);
	if (hasOwn(change, 'changeGroupId')) {
		if (!hasIdentifier(change.changeGroupId))
			invalid(`${path}.changeGroupId no es compatible.`);
		if (change.changeType !== 'SERIAL_SUBSTITUTED')
			invalid(`${path}.changeGroupId no corresponde al tipo de cambio.`);
	}
	if (change.original !== null)
		validateInventoryChangeSide(change.original, `${path}.original`);
	if (change.target !== null)
		validateInventoryChangeSide(change.target, `${path}.target`);
	if (change.original === null && change.target === null)
		invalid(`${path} no contiene inventario original ni corregido.`);
	if (change.kind === 'BULK') {
		if (
			change.series !== null ||
			change.original === null ||
			change.target === null
		)
			invalid(`${path} no representa un cambio contabilizado válido.`);
	} else if (typeof change.series !== 'string' || !change.series.trim()) {
		invalid(`${path}.series es requerida para una serie física.`);
	}
	if (change.kind === 'SERIAL_ADDED' && change.original !== null)
		invalid(`${path}.original debe ser null para una serie agregada.`);
	if (change.kind === 'SERIAL_REMOVED' && change.target !== null)
		invalid(`${path}.target debe ser null para una serie removida.`);
	if (
		['SERIAL_RECATALOGED', 'SERIAL_UPDATED'].includes(change.kind) &&
		(change.original === null || change.target === null)
	)
		invalid(`${path} requiere inventario original y corregido.`);
};

const validateCurrentInventoryItem = (entry, path) => {
	if (!isRecord(entry)) invalid(`${path} debe ser un objeto.`);
	if (!CURRENT_INVENTORY_KINDS.has(entry.kind))
		invalid(`${path}.kind no es compatible.`);
	if (entry.seriesId !== null && !hasIdentifier(entry.seriesId))
		invalid(`${path}.seriesId no es compatible.`);
	if (entry.unit !== null) {
		if (!isRecord(entry.unit)) invalid(`${path}.unit no es compatible.`);
		if (!hasIdentifier(entry.unit.id)) invalid(`${path}.unit.id es requerida.`);
		if (!hasIdentifier(entry.unit.code))
			invalid(`${path}.unit.code es requerida.`);
	}
	if (!isRecord(entry.current)) invalid(`${path}.current es requerido.`);
	if (!isRecord(entry.current.item))
		invalid(`${path}.current.item es requerido.`);
	if (!hasIdentifier(entry.current.item.itemId))
		invalid(`${path}.current.item.itemId es requerido.`);
	if (!hasIdentifier(entry.current.item.code))
		invalid(`${path}.current.item.code es requerido.`);
	if (!hasIdentifier(entry.current.item.label))
		invalid(`${path}.current.item.label es requerido.`);
	if (entry.current.state !== null) {
		if (!isRecord(entry.current.state))
			invalid(`${path}.current.state no es compatible.`);
		if (!hasIdentifier(entry.current.state.id))
			invalid(`${path}.current.state.id es requerido.`);
		if (!hasIdentifier(entry.current.state.label))
			invalid(`${path}.current.state.label es requerido.`);
	}
	if (!Number.isFinite(entry.current.value))
		invalid(`${path}.current.value debe ser numérico.`);

	if (entry.kind === 'BULK') {
		if (entry.series !== null || entry.seriesId !== null)
			invalid(`${path} no representa inventario contabilizado válido.`);
		if (entry.current.value <= 0)
			invalid(`${path}.current.value debe ser positivo para BULK.`);
	} else {
		if (typeof entry.series !== 'string' || !entry.series.trim())
			invalid(`${path}.series es requerida para una serie física.`);
		if (entry.current.value !== 1)
			invalid(`${path}.current.value debe ser 1 para SERIAL.`);
	}
};

const validateCurrentInventory = (snapshot, evaluationSha256, path) => {
	if (!isRecord(snapshot)) invalid(`${path} debe ser un objeto.`);
	if (typeof snapshot.available !== 'boolean')
		invalid(`${path}.available debe ser booleano.`);
	if (!Array.isArray(snapshot.items))
		invalid(`${path}.items debe ser un arreglo.`);

	if (snapshot.available) {
		if (snapshot.reason !== null)
			invalid(`${path}.reason debe ser null si está disponible.`);
		if (!isValidMaterialEvaluationSha256(snapshot.sourceContentSha256))
			invalid(`${path}.sourceContentSha256 no es un SHA-256 válido.`);
		if (snapshot.sourceContentSha256 !== evaluationSha256)
			invalid(`${path}.sourceContentSha256 no coincide con la evaluación.`);
		snapshot.items.forEach((entry, index) =>
			validateCurrentInventoryItem(entry, `${path}.items[${index}]`),
		);
		return;
	}

	if (!CURRENT_INVENTORY_UNAVAILABLE_REASONS.has(snapshot.reason))
		invalid(`${path}.reason no es compatible.`);
	if (
		snapshot.sourceContentSha256 !== undefined &&
		snapshot.sourceContentSha256 !== null
	)
		invalid(
			`${path}.sourceContentSha256 debe omitirse si no está disponible.`,
		);
	if (snapshot.items.length > 0)
		invalid(`${path}.items debe estar vacío si no está disponible.`);
};

// This read-only projection is independent of approval evidence and capabilities.
const positiveId = value => Number.isSafeInteger(value) && value > 0;
const nonEmptyText = value => typeof value === 'string' && !!value.trim();
const validTimestamp = value =>
	typeof value === 'string' &&
	ISO_DATE_TIME_PATTERN.test(value) &&
	Number.isFinite(Date.parse(value));

export const getOrderCurrentInventory = viewModel => {
	if (!hasOwn(viewModel || {}, 'currentInventory')) return undefined;
	const snapshot = viewModel.currentInventory;
	try {
		if (
			!isRecord(snapshot) ||
			snapshot.source !== 'WTD' ||
			!validTimestamp(snapshot.capturedAt) ||
			(snapshot.updatedAt !== null && !validTimestamp(snapshot.updatedAt))
		)
			invalid('currentInventory no identifica una captura WTD válida.');
		if (snapshot.reason === 'CURRENT_WTD_NOT_FOUND') {
			if (
				snapshot.available !== false ||
				snapshot.wtdId !== null ||
				snapshot.wtdStatus !== null ||
				snapshot.updatedAt !== null ||
				snapshot.sourceContentSha256 !== null ||
				!Array.isArray(snapshot.items) ||
				snapshot.items.length !== 0
			)
				invalid('currentInventory no representa ausencia de WTD.');
			return snapshot;
		}
		if (
			!positiveId(snapshot.wtdId) ||
			!['OPEN', 'CLOSED'].includes(snapshot.wtdStatus)
		)
			invalid('currentInventory no identifica el documento WTD.');
		if (snapshot.available === false && snapshot.sourceContentSha256 !== null)
			invalid(
				'currentInventory.sourceContentSha256 debe ser null si no está disponible.',
			);
		validateCurrentInventory(
			snapshot,
			snapshot.sourceContentSha256,
			'currentInventory',
		);
		snapshot.items.forEach(entry => {
			if (
				!positiveId(entry.current.item.itemId) ||
				!nonEmptyText(entry.current.item.code) ||
				!nonEmptyText(entry.current.item.label) ||
				(entry.seriesId !== null && !positiveId(entry.seriesId)) ||
				(entry.unit !== null &&
					(!(positiveId(entry.unit.id) || entry.unit.id === 'UNKNOWN') ||
						!nonEmptyText(entry.unit.code))) ||
				(entry.current.state !== null &&
					(!positiveId(entry.current.state.id) ||
						!nonEmptyText(entry.current.state.label)))
			)
				invalid(
					'currentInventory contiene una identidad de material inválida.',
				);
		});
		return snapshot;
	} catch (error) {
		if (!(error instanceof InvalidMaterialValidationContractError)) throw error;
		return {
			available: false,
			reason: 'CURRENT_INVENTORY_CONTRACT_INVALID',
			items: [],
			sourceContentSha256: null,
		};
	}
};

const inventoryChangeItemId = change =>
	change.original?.item?.itemId ?? change.target?.item?.itemId;

const inventoryChangeSeriesKey = change =>
	change.seriesId !== null && change.seriesId !== undefined
		? `id:${change.seriesId}`
		: `label:${change.series}`;

const validatePairedInventoryChanges = (changes, path) => {
	const pairedChanges = new Map();
	changes.forEach((change, index) => {
		if (!['STATE_CHANGED', 'SERIAL_SUBSTITUTED'].includes(change.changeType))
			return;
		const identity =
			change.changeType === 'STATE_CHANGED'
				? inventoryChangeSeriesKey(change)
				: hasOwn(change, 'changeGroupId')
					? `group:${change.changeGroupId}`
					: `item:${inventoryChangeItemId(change)}`;
		const key = `${change.changeType}:${identity}`;
		const entries = pairedChanges.get(key) || [];
		entries.push({ change, index });
		pairedChanges.set(key, entries);
	});
	pairedChanges.forEach((entries, key) => {
		const removed = entries.filter(
			entry => entry.change.kind === 'SERIAL_REMOVED',
		);
		const added = entries.filter(entry => entry.change.kind === 'SERIAL_ADDED');
		const changeType = entries[0].change.changeType;
		const hasExplicitGroup = hasOwn(entries[0].change, 'changeGroupId');
		const hasValidCardinality =
			changeType === 'SERIAL_SUBSTITUTED' && !hasExplicitGroup
				? removed.length > 0 &&
					removed.length === added.length &&
					entries.length === removed.length + added.length
				: entries.length === 2 && removed.length === 1 && added.length === 1;
		if (!hasValidCardinality)
			invalid(`${path} no contiene un par de cambios válido para ${key}.`);
		if (
			removed.some(entry => entry.change.delta !== -1) ||
			added.some(entry => entry.change.delta !== 1)
		)
			invalid(`${path} contiene un delta de cambio inválido para ${key}.`);
		const itemIds = new Set(
			entries.map(entry => inventoryChangeItemId(entry.change)),
		);
		if (itemIds.size !== 1)
			invalid(`${path} empareja materiales diferentes para ${key}.`);
		if (changeType === 'SERIAL_SUBSTITUTED') {
			const removedSeries = removed.map(entry =>
				inventoryChangeSeriesKey(entry.change),
			);
			const addedSeries = added.map(entry =>
				inventoryChangeSeriesKey(entry.change),
			);
			if (
				new Set(removedSeries).size !== removedSeries.length ||
				new Set(addedSeries).size !== addedSeries.length ||
				removedSeries.some(series => addedSeries.includes(series))
			)
				invalid(`${path} no sustituye series únicas para ${key}.`);
		}
	});
};

const validateCurrentEvaluation = (
	evaluation,
	path,
	{ requireInventoryChanges = false, requireAdjustmentTarget = false } = {},
) => {
	if (!isRecord(evaluation)) invalid(`${path} debe ser un objeto.`);
	if (!MATERIAL_VALIDATION_EVALUATION_STATUSES.has(evaluation.status))
		invalid(`${path}.status no es compatible.`);
	if (!Array.isArray(evaluation.comparisons))
		invalid(`${path}.comparisons debe ser un arreglo.`);
	evaluation.comparisons.forEach((comparison, index) =>
		validateComparison(comparison, `${path}.comparisons[${index}]`),
	);
	if (!isValidMaterialEvaluationSha256(evaluation.contentSha256))
		invalid(`${path}.contentSha256 no es un SHA-256 válido.`);
	if (requireAdjustmentTarget && evaluation.targetIsAdjustment !== true)
		invalid(`${path}.targetIsAdjustment debe identificar el ajuste vigente.`);
	const hasInventoryChanges = hasOwn(evaluation, 'inventoryChanges');
	const hasInventoryAudit = hasOwn(evaluation, 'inventoryAudit');
	const hasCurrentInventory = hasOwn(evaluation, 'currentInventory');
	if (
		(requireInventoryChanges || hasInventoryChanges || hasInventoryAudit) &&
		!Array.isArray(evaluation.inventoryChanges)
	)
		invalid(`${path}.inventoryChanges debe ser un arreglo.`);
	if (Array.isArray(evaluation.inventoryChanges)) {
		evaluation.inventoryChanges.forEach((change, index) =>
			validateInventoryChange(change, `${path}.inventoryChanges[${index}]`),
		);
		validatePairedInventoryChanges(
			evaluation.inventoryChanges,
			`${path}.inventoryChanges`,
		);
	}
	if (hasInventoryAudit) {
		const audit = evaluation.inventoryAudit;
		if (!isRecord(audit)) invalid(`${path}.inventoryAudit debe ser un objeto.`);
		if (typeof audit.available !== 'boolean')
			invalid(`${path}.inventoryAudit.available debe ser booleano.`);
		if (audit.available === true && audit.reason !== null)
			invalid(`${path}.inventoryAudit.reason debe ser null si está disponible.`);
		if (
			audit.available === false &&
			(typeof audit.reason !== 'string' || !audit.reason.trim())
		)
			invalid(`${path}.inventoryAudit.reason es requerida si no está disponible.`);
		if (audit.available === false && evaluation.inventoryChanges.length > 0)
			invalid(`${path}.inventoryChanges debe estar vacío si la auditoría no está disponible.`);
	}
	if (hasCurrentInventory)
		validateCurrentInventory(
			evaluation.currentInventory,
			evaluation.contentSha256,
			`${path}.currentInventory`,
		);
};

const validateManualCorrectionRequestCapability = (capability, path) => {
	if (!isRecord(capability)) invalid(`${path} debe ser un objeto.`);
	if (typeof capability.allowed !== 'boolean')
		invalid(`${path}.allowed debe ser booleano.`);
	if (!Array.isArray(capability.reasons))
		invalid(`${path}.reasons debe ser un arreglo.`);
	capability.reasons.forEach((reason, index) => {
		const reasonPath = `${path}.reasons[${index}]`;
		if (!isRecord(reason)) invalid(`${reasonPath} debe ser un objeto.`);
		if (
			typeof reason.code !== 'string' ||
			!REASON_CODE_PATTERN.test(reason.code)
		)
			invalid(`${reasonPath}.code no es compatible.`);
		if (typeof reason.message !== 'string' || !reason.message.trim())
			invalid(`${reasonPath}.message es requerido.`);
	});
	if (capability.allowed) {
		if (capability.mode !== 'INVENTORY_ADJUSTMENT')
			invalid(`${path}.mode no es compatible para una solicitud permitida.`);
		if (capability.reasons.length > 0)
			invalid(
				`${path}.reasons debe estar vacío cuando la solicitud se permite.`,
			);
	} else if (capability.mode !== null) {
		invalid(`${path}.mode debe ser null cuando la solicitud no se permite.`);
	}
};

const sameMetricKey = (left, right) => String(left) === String(right);

export const getApprovalQuantitySelectionCapability = viewModel =>
	viewModel?.capabilities?.approvalQuantitySelection;

const validateApprovalQuantitySelectionCapability = (value, capability) => {
	const path = 'capabilities.approvalQuantitySelection';
	if (!isRecord(capability)) invalid(`${path} debe ser un objeto.`);
	if (capability.required !== true)
		invalid(
			`${path}.required debe ser true cuando la capability está presente.`,
		);
	if (capability.mode !== 'PER_COMPARISON')
		invalid(`${path}.mode no es compatible.`);
	if (!isValidMaterialEvaluationSha256(capability.pricingPolicySha256))
		invalid(`${path}.pricingPolicySha256 no es un SHA-256 válido.`);
	if (!Array.isArray(capability.items) || capability.items.length === 0)
		invalid(`${path}.items debe contener comparaciones seleccionables.`);
	const evaluationSha256 =
		value.currentEvaluation?.contentSha256 ||
		value.currentCase?.input?.contentSha256;
	if (!isValidMaterialEvaluationSha256(evaluationSha256))
		invalid(`${path} requiere un hash de evidencia vigente.`);

	const comparisons = requiresLiveMaterialEvaluation(
		value.currentCase,
		value.currentEvaluation,
	)
		? value.currentEvaluation?.comparisons || []
		: value.currentCase?.comparisons || [];
	const seenMetricKeys = new Set();
	capability.items.forEach((item, index) => {
		const itemPath = `${path}.items[${index}]`;
		if (!isRecord(item)) invalid(`${itemPath} debe ser un objeto.`);
		if (!hasIdentifier(item.metricKey))
			invalid(`${itemPath}.metricKey es requerido.`);
		const normalizedMetricKey = String(item.metricKey);
		if (seenMetricKeys.has(normalizedMetricKey))
			invalid(`${path}.items contiene metricKey duplicado.`);
		seenMetricKeys.add(normalizedMetricKey);

		const matchingComparisons = comparisons.filter(candidate =>
			sameMetricKey(candidate.metricKey, item.metricKey),
		);
		if (matchingComparisons.length !== 1)
			invalid(`${itemPath}.metricKey no existe en la evidencia vigente.`);
		const comparison = matchingComparisons[0];
		if (comparison.subject.kind !== 'ITEM' || comparison.outcome !== 'MISMATCH')
			invalid(`${itemPath} debe referenciar un material discrepante.`);
		if (comparison.unit.code === 'BOOLEAN')
			invalid(`${itemPath} no puede referenciar una comparación BOOLEAN.`);
		if (!isRecord(item.unit) || typeof item.unit.code !== 'string')
			invalid(`${itemPath}.unit es requerida.`);
		if (
			String(item.unit.code) !== String(comparison.unit.code) ||
			String(item.unit.id) !== String(comparison.unit.id)
		)
			invalid(`${itemPath}.unit no coincide con la comparación.`);
		if (
			!Array.isArray(item.strategies) ||
			item.strategies.length !== APPROVAL_QUANTITY_STRATEGIES.size ||
			new Set(item.strategies).size !== item.strategies.length ||
			item.strategies.some(
				strategy => !APPROVAL_QUANTITY_STRATEGIES.has(strategy),
			)
		)
			invalid(`${itemPath}.strategies no contiene las opciones requeridas.`);
		if (hasOwn(item, 'previews')) {
			if (!isRecord(item.previews))
				invalid(`${itemPath}.previews debe ser un objeto.`);
			const previewStrategies = Object.keys(item.previews);
			if (
				previewStrategies.length !== 2 ||
				!previewStrategies.includes('PAY_EXPECTED') ||
				!previewStrategies.includes('PAY_REPORTED')
			)
				invalid(`${itemPath}.previews no contiene las estrategias esperadas.`);
			['PAY_EXPECTED', 'PAY_REPORTED'].forEach(strategy => {
				const preview = item.previews[strategy];
				const previewPath = `${itemPath}.previews.${strategy}`;
				if (!isRecord(preview)) invalid(`${previewPath} debe ser un objeto.`);
				['selectedValue', 'resolvedValue'].forEach(key => {
					if (
						typeof preview[key] !== 'string' ||
						!CANONICAL_DECIMAL_PATTERN.test(preview[key])
					)
						invalid(`${previewPath}.${key} no es un decimal canónico.`);
				});
				const comparisonValue =
					strategy === 'PAY_EXPECTED'
						? comparison.expectedValue
						: comparison.reportedValue;
				if (preview.selectedValue !== String(comparisonValue))
					invalid(`${previewPath}.selectedValue no coincide con la evidencia.`);
			});
		}

		const constraints = item.adjustedValueConstraints;
		if (!isRecord(constraints))
			invalid(`${itemPath}.adjustedValueConstraints es requerido.`);
		if (!Number.isFinite(constraints.minValue) || constraints.minValue < 0)
			invalid(`${itemPath}.adjustedValueConstraints.minValue no es válido.`);
		if (
			hasOwn(constraints, 'maxValue') &&
			constraints.maxValue !== null &&
			(!Number.isFinite(constraints.maxValue) ||
				constraints.maxValue < constraints.minValue)
		)
			invalid(`${itemPath}.adjustedValueConstraints.maxValue no es válido.`);
		if (
			!Number.isSafeInteger(constraints.decimalPlaces) ||
			constraints.decimalPlaces < 0 ||
			constraints.decimalPlaces > 6
		)
			invalid(
				`${itemPath}.adjustedValueConstraints.decimalPlaces no es válido.`,
			);
		if (hasOwn(constraints, 'gates')) {
			if (!Array.isArray(constraints.gates))
				invalid(
					`${itemPath}.adjustedValueConstraints.gates debe ser un arreglo.`,
				);
			constraints.gates.forEach((gate, gateIndex) => {
				const gatePath = `${itemPath}.adjustedValueConstraints.gates[${gateIndex}]`;
				if (!isRecord(gate)) invalid(`${gatePath} debe ser un objeto.`);
				if (!['INVENTORY', 'IMPORTED'].includes(gate.source))
					invalid(`${gatePath}.source no es compatible.`);
				if (typeof gate.signalKey !== 'string' || !gate.signalKey.trim())
					invalid(`${gatePath}.signalKey es requerido.`);
				if (typeof gate.satisfied !== 'boolean')
					invalid(`${gatePath}.satisfied debe ser booleano.`);
				if (
					typeof gate.resolvedValueWhenBlocked !== 'string' ||
					!CANONICAL_DECIMAL_PATTERN.test(gate.resolvedValueWhenBlocked)
				)
					invalid(`${gatePath}.resolvedValueWhenBlocked no es válido.`);
			});
		}
	});
};

const validateManualAdjustmentSubmission = (submission, path) => {
	if (!isRecord(submission)) invalid(`${path} debe ser un objeto.`);
	if (
		typeof submission.submittedAt !== 'string' ||
		!ISO_DATE_TIME_PATTERN.test(submission.submittedAt) ||
		!Number.isFinite(Date.parse(submission.submittedAt))
	)
		invalid(`${path}.submittedAt no es una fecha ISO-8601 válida.`);
	if (
		!Number.isSafeInteger(submission.targetWtdId) ||
		submission.targetWtdId < 1
	)
		invalid(`${path}.targetWtdId debe ser un entero positivo.`);
	if (!isValidMaterialEvaluationSha256(submission.evaluationSha256))
		invalid(`${path}.evaluationSha256 no es un SHA-256 válido.`);
	if (!isValidMaterialEvaluationSha256(submission.targetContentSha256))
		invalid(`${path}.targetContentSha256 no es un SHA-256 válido.`);
};

const validateManualAdjustmentWorkflow = value => {
	if (!hasOwn(value.workflow, 'manualAdjustment')) return;
	const manualAdjustment = value.workflow.manualAdjustment;
	const manualCase = isManualCorrectionCase(value.currentCase);
	if (!manualCase) {
		if (manualAdjustment !== null)
			invalid(
				'workflow.manualAdjustment debe ser null cuando el caso no es manual.',
			);
		return;
	}
	if (value.currentCase.status !== 'PENDING_REVIEW') {
		if (manualAdjustment !== null)
			invalid(
				'workflow.manualAdjustment debe ser null cuando el caso manual terminó.',
			);
		return;
	}
	if (!isRecord(manualAdjustment))
		invalid('workflow.manualAdjustment debe describir el ajuste manual.');
	if (manualAdjustment.contractVersion !== 1)
		invalid('workflow.manualAdjustment.contractVersion no es compatible.');
	if (!['EDITING', 'READY_FOR_REVIEW'].includes(manualAdjustment.state))
		invalid('workflow.manualAdjustment.state no es compatible.');
	if (
		!Number.isSafeInteger(manualAdjustment.caseVersion) ||
		manualAdjustment.caseVersion < 1 ||
		manualAdjustment.caseVersion !== value.currentCase.version
	)
		invalid(
			'workflow.manualAdjustment.caseVersion no coincide con el caso vigente.',
		);

	if (manualAdjustment.state === 'EDITING') {
		if (!isConfirmedManualCorrectionCase(value.currentCase))
			invalid(
				'workflow.manualAdjustment EDITING no coincide con el caso vigente.',
			);
		if (manualAdjustment.submission !== null)
			invalid('workflow.manualAdjustment.submission debe ser null en EDITING.');
		if (value.currentEvaluation !== null)
			invalid('currentEvaluation debe ser null mientras el técnico edita.');
		if (value.capabilities.canDecide || value.capabilities.decisions.length > 0)
			invalid('No deben existir decisiones administrativas durante EDITING.');
		return;
	}

	validateManualAdjustmentSubmission(
		manualAdjustment.submission,
		'workflow.manualAdjustment.submission',
	);
	if (!isRecord(value.currentEvaluation))
		invalid('currentEvaluation es requerida en READY_FOR_REVIEW.');
	if (
		value.currentEvaluation.targetIsAdjustment !== true ||
		!Array.isArray(value.currentEvaluation.inventoryChanges) ||
		value.currentEvaluation.inventoryChanges.length === 0
	)
		invalid('currentEvaluation no contiene un ajuste manual revisable.');
	if (
		!Number.isSafeInteger(value.currentEvaluation.targetWtdId) ||
		value.currentEvaluation.targetWtdId < 1 ||
		value.currentEvaluation.targetWtdId !==
			manualAdjustment.submission.targetWtdId
	)
		invalid(
			'currentEvaluation.targetWtdId no coincide con la entrega vigente.',
		);
	if (
		value.currentEvaluation.contentSha256 !==
		manualAdjustment.submission.evaluationSha256
	)
		invalid(
			'currentEvaluation.contentSha256 no coincide con la entrega vigente.',
		);
	if (
		!isRecord(value.currentEvaluation.targetSeal) ||
		!isValidMaterialEvaluationSha256(
			value.currentEvaluation.targetSeal.contentSha256,
		) ||
		value.currentEvaluation.targetSeal.contentSha256 !==
			manualAdjustment.submission.targetContentSha256
	)
		invalid('currentEvaluation.targetSeal no coincide con la entrega vigente.');
};

export const validateMaterialValidationViewModel = value => {
	if (!value || typeof value !== 'object' || Array.isArray(value))
		invalid('La respuesta debe ser un objeto.');
	if (value.contractVersion !== 1) invalid('contractVersion no es compatible.');
	if (!hasIdentifier(value.orderId)) invalid('orderId es requerido.');
	if (typeof value.required !== 'boolean')
		invalid('required debe ser booleano.');
	if (
		!value.workflow ||
		typeof value.workflow !== 'object' ||
		Array.isArray(value.workflow)
	)
		invalid('workflow es requerido.');
	if (!Array.isArray(value.previousCases))
		invalid('previousCases debe ser un arreglo.');
	if (!value.consistency || typeof value.consistency !== 'object')
		invalid('consistency es requerida.');
	if (!MATERIAL_VALIDATION_CONSISTENCY_STATES.has(value.consistency.state))
		invalid('consistency.state no es compatible.');
	if (!Array.isArray(value.consistency.issues))
		invalid('consistency.issues debe ser un arreglo.');
	if (!value.capabilities || typeof value.capabilities !== 'object')
		invalid('capabilities es requerido.');
	if (!Object.prototype.hasOwnProperty.call(value, 'currentCase'))
		invalid('currentCase es requerido.');

	if (value.currentCase !== null)
		validateCase(value.currentCase, 'currentCase', { requireVersion: true });
	if (
		requiresLiveMaterialEvaluation(value.currentCase, value.currentEvaluation)
	) {
		const isManualAdjustment = isManualCorrectionCase(value.currentCase);
		validateCurrentEvaluation(value.currentEvaluation, 'currentEvaluation', {
			requireInventoryChanges:
				value.currentCase?.resolutionKey ===
					GPON_INVENTORY_RECONCILIATION_RESOLUTION_KEY || isManualAdjustment,
			requireAdjustmentTarget: isManualAdjustment,
		});
	} else if (value.currentEvaluation != null) {
		validateCurrentEvaluation(value.currentEvaluation, 'currentEvaluation');
	}
	value.previousCases.forEach((materialCase, index) =>
		validateCase(materialCase, `previousCases[${index}]`),
	);

	if (typeof value.capabilities.canDecide !== 'boolean')
		invalid('capabilities.canDecide debe ser booleano.');
	if (!Array.isArray(value.capabilities.decisions))
		invalid('capabilities.decisions debe ser un arreglo.');
	if (
		!Object.prototype.hasOwnProperty.call(
			value.capabilities,
			'manualCorrectionRequest',
		)
	)
		invalid('capabilities.manualCorrectionRequest es requerido.');
	validateManualCorrectionRequestCapability(
		value.capabilities.manualCorrectionRequest,
		'capabilities.manualCorrectionRequest',
	);
	value.capabilities.decisions.forEach(decision => {
		if (!MATERIAL_VALIDATION_DECISIONS.has(decision))
			invalid('capabilities.decisions contiene una decisión no compatible.');
	});
	if (hasOwn(value.capabilities, 'approvalQuantitySelection')) {
		if (!value.capabilities.decisions.includes('APPROVE'))
			invalid(
				'capabilities.approvalQuantitySelection requiere la decisión APPROVE.',
			);
		validateApprovalQuantitySelectionCapability(
			value,
			value.capabilities.approvalQuantitySelection,
		);
	}
	if (
		value.capabilities.canDecide &&
		!hasIdentifier(value.capabilities.expectedVersion)
	)
		invalid('capabilities.expectedVersion es requerida para decidir.');
	if (
		!value.capabilities.canDecide &&
		value.capabilities.expectedVersion !== null
	)
		invalid('capabilities.expectedVersion debe ser null en solo lectura.');

	validateManualAdjustmentWorkflow(value);

	const currentInventory = getOrderCurrentInventory(value);
	return currentInventory === value.currentInventory
		? value
		: { ...value, currentInventory };
};
